import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency } from '@/lib/formatUtils';
import type { ShiftPerformance } from '@/hooks/useDashboardInsights';

interface ShiftPerformanceCardProps {
  topShifts: ShiftPerformance[];
  bottomShifts: ShiftPerformance[];
  footerText?: string;
}

export const ShiftPerformanceCard: React.FC<ShiftPerformanceCardProps> = ({
  topShifts,
  bottomShifts,
  footerText = 'Use this to inform staffing and operating hours.',
}) => {
  return (
    <Card className="bg-card">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            When Do I Sell
          </span>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-6">
          {/* Top 3 Shifts */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-medium text-muted-foreground">
                Top 3 Shifts
              </span>
            </div>
            <div className="space-y-3">
              {topShifts.map((shift) => (
                <div key={shift.shift} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">
                    {shift.shift}
                  </span>
                  <span className="text-sm font-semibold font-mono">
                    {formatCurrency(shift.revenue)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom 3 Shifts */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="h-4 w-4 text-amber-600" />
              <span className="text-xs font-medium text-muted-foreground">
                Bottom 3 Shifts
              </span>
            </div>
            <div className="space-y-3">
              {bottomShifts.map((shift) => (
                <div key={shift.shift} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">
                    {shift.shift}
                  </span>
                  <span className="text-sm font-semibold font-mono">
                    {formatCurrency(shift.revenue)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        {footerText && (
          <p className="text-sm text-muted-foreground mt-6">
            {footerText}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
